/**
 * Temperature Role - Format Pillar
 *
 * Formata valores de temperatura para apresentação.
 *
 * @example
 * import { formatTemperature } from '@attrx/role-morphic/temperature/format';
 *
 * formatTemperature('celsius', 37);                        // "37°C"
 * formatTemperature('fahrenheit', 98.6, { verbose: true }); // "98.6 degrees Fahrenheit"
 * formatTemperature('kelvin', 273.15);                     // "273.15 K"
 */

import { TEMPERATURE_UNITS, type TemperatureUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface TemperatureFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de temperatura para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatTemperature('celsius', 37);                          // "37°C"
 * formatTemperature('fahrenheit', 98.6);                     // "98.6°F"
 * formatTemperature('kelvin', 273.15);                       // "273.15 K"
 * formatTemperature('celsius', 37, { verbose: true });       // "37 degrees Celsius"
 * formatTemperature('celsius', 1000, { locale: 'pt-BR' });   // "1.000°C"
 */
export function formatTemperature(
  variant: TemperatureUnit,
  value: number,
  options: TemperatureFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = TEMPERATURE_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown temperature variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  // Nota: °C, °F, °R não têm espaço (noSpace: true), mas K tem espaço
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getTemperatureSymbol('celsius');     // "°C"
 * getTemperatureSymbol('fahrenheit');  // "°F"
 * getTemperatureSymbol('kelvin');      // "K"
 */
export function getTemperatureSymbol(variant: TemperatureUnit): string {
  const unit = TEMPERATURE_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown temperature variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getTemperatureName('celsius', 1);   // "degree Celsius"
 * getTemperatureName('celsius', 2);   // "degrees Celsius"
 * getTemperatureName('kelvin', 1);    // "kelvin"
 * getTemperatureName('kelvin', 2);    // "kelvins"
 */
export function getTemperatureName(variant: TemperatureUnit, count: number = 1): string {
  const unit = TEMPERATURE_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown temperature variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
